import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { EyeIcon } from '@heroicons/react/24/solid'
import { EyeSlashIcon } from '@heroicons/react/24/solid'
import { Button } from '@/components/ui/button'
import { useCADPageStore } from '@/state/cad'
import { cn } from '@/utils'
import { useAssemblyTreeQuery } from '../../queries'
import { useGetActiveStepInclusiveGroupIds } from '@/services/hooks/steps'

import type { GLTFObject } from '@/lib/cad/GLTFObject'
import type { ASMTreeNode } from '@/state'
import { getAncestors, getDescendants } from '@/state/utils/assembly'

export const HidePartButton = ({
  node,
  gltf,
  isVisible,
}: {
  node: ASMTreeNode
  gltf: GLTFObject
  isVisible: boolean
}) => {
  const {
    isLoading: isAssemblyTreeLoading,
    data: { assemblyTree },
  } = useAssemblyTreeQuery()
  const { isLoading: isLoadingInclusiveGroupIds } =
    useGetActiveStepInclusiveGroupIds()
  const isLoading = isAssemblyTreeLoading || isLoadingInclusiveGroupIds

  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))

  const isHidden = hiddenParts.includes(node.uuid)

  const descendantNodes = useMemo(() => {
    if (!assemblyTree) return []

    return getDescendants(assemblyTree, node)
  }, [assemblyTree, node])

  const ancestorNodes = useMemo(() => {
    if (!assemblyTree) return []

    return getAncestors(assemblyTree, node)
  }, [assemblyTree, node])

  const toggleHiddenPart = useCallback(() => {
    const shouldShow = !!isHidden

    const nodes = Array.from(
      new Set(
        shouldShow
          ? [node, ...ancestorNodes, ...descendantNodes]
          : [node, ...descendantNodes],
      ),
    )

    nodes.forEach((n) => {
      gltf.setVisibility(n.uuid, shouldShow)
    })

    // Update hidden parts state
    const nodeUUIDs = nodes.map((n) => n.uuid)
    setCadPageState({
      hiddenParts: isHidden
        ? hiddenParts.filter((uuid) => !nodeUUIDs.includes(uuid))
        : [...hiddenParts, ...nodeUUIDs],
    })
  }, [
    gltf,
    node,
    isHidden,
    hiddenParts,
    ancestorNodes,
    setCadPageState,
    descendantNodes,
  ])

  const Icon = isHidden ? EyeSlashIcon : EyeIcon
  const shouldShowButton = isVisible || isHidden

  if (isLoading) return null

  return (
    <Button
      className="h-auto w-auto p-1 min-h-[24px]"
      variant="ghost"
      onMouseDown={(e) => {
        e.stopPropagation()
        if (shouldShowButton) {
          toggleHiddenPart()
        }
      }}
    >
      <Icon
        className={cn('w-4 h-4', {
          hidden: !shouldShowButton,
          'text-primary-50': !isHidden,
          'text-gray-400': isHidden,
        })}
      />
    </Button>
  )
}
